import { inject } from '@angular/core';
import { Observable, switchMap, timer } from 'rxjs';
import { Endpoints, NotificationType } from '@core/enums/notifications.enum';
import { GetUserId } from '../utils';
import { AcceptDenyService } from '../services/accept-deny.service';
import { ApprovedStatus } from '../enums';

export class UseNotification {
  private readonly _acceptDenyService = inject(AcceptDenyService);
  private readonly _userId = +GetUserId();

  startPollingNotifications(): Observable<any> {
    return timer(0, 30000).pipe(switchMap(() => ''));
  }

  getEndpointForDenyReason(status: ApprovedStatus, section: string): string {
    if (status === ApprovedStatus.UnApproved) {
      switch (section) {
        case NotificationType.Group:
          return Endpoints.GROUP_DELETE;
        case NotificationType.Task:
          return Endpoints.TASK_DELETE;
        case NotificationType.Instructor:
          return Endpoints.INSTRUCTOR_DELETE;
        case NotificationType.Room:
          return Endpoints.ROOM_DELETE;
        case NotificationType.Survey:
          return Endpoints.SURVEY_DELETE;
        case NotificationType.Event:
          return Endpoints.EVENT_DELETE;
        case NotificationType.Course:
          return Endpoints.COURSE_DELETE;
        case NotificationType.FAQ:
          return Endpoints.FAQS_DELETE;
        default:
          throw new Error('Invalid section');
      }
    } else if (status === ApprovedStatus.Approved) {
      switch (section) {
        case NotificationType.Group:
          return Endpoints.GROUP_GET;
        case NotificationType.Task:
          return Endpoints.TASK_GET;
        case NotificationType.Instructor:
          return Endpoints.INSTRUCTOR_GET;
        case NotificationType.Room:
          return Endpoints.ROOM_GET;
        case NotificationType.Survey:
          return Endpoints.SURVEY_GET;
        case NotificationType.Event:
          return Endpoints.EVENT_GET;
        case NotificationType.Course:
          return Endpoints.COURSE_GET;
        case NotificationType.FAQ:
          return Endpoints.FAQS_GET;
        default:
          throw new Error('Invalid section');
      }
    }
    throw new Error('Invalid status');
  }

  // Deny Reason confirmation
  denyReasonConfirmation(endpoint: any) {
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.denyReason(endpoint, this._userId);
  }

  handleNewsApproval(deleteId: number) {
    if (!deleteId) throw new Error('deleteId is required');
    return this._acceptDenyService.handleNewsApproval(deleteId);
  }

  handleNewsDisapproval(deleteId: number) {
    if (!deleteId) throw new Error('deleteId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.handleNewsDisapproval(deleteId, this._userId);
  }

  SectionDataDenyReason(endpoint: any, deleteId: number) {
    if (!deleteId) throw new Error('deleteId is required');
    return this._acceptDenyService.SectionDataDenyReason(endpoint, deleteId);
  }

  /**
   * These below notification action functions and names are adapted and copied from Legacy code.
   */

  // ----------------------------------------------------------------------------------
  // ------------------------------------ GROUPS --------------------------------------
  // ----------------------------------------------------------------------------------

  joinGroup(groupId: number, notificationUserId: number): Observable<any> {
    if (!groupId) throw new Error('groupId is required');
    if (!notificationUserId) throw new Error('notificationUserId is required');
    const postData: object = {
      participants: {
        group_id: groupId,
        user_id: notificationUserId,
        approved_status: 1,
      },
    };
    return this._acceptDenyService.joinGroup(groupId, notificationUserId, postData);
  }

  acceptGroup(groupId: number): Observable<any> {
    if (!groupId) throw new Error('groupId is required');
    if (!this._userId) throw new Error('userId is required');

    const postData: object = {
      participants: {
        group_id: groupId,
        user_id: this._userId,
        approved_status: 1,
      },
    };
    return this._acceptDenyService.joinGroup(groupId, this._userId, postData);
  }

  denyGroup(groupId: number, otherUserId: number): Observable<any> {
    if (!groupId) throw new Error('groupId is required');
    if (!otherUserId) throw new Error('otherUserId is required');
    return this._acceptDenyService.deny(groupId, otherUserId);
  }

  updateGroup(groupId: number): Observable<any> {
    if (!groupId) throw new Error('groupId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.acceptUpdatedGroup(groupId, this._userId);
  }

  viewGroup(groupId: number): Observable<any> {
    if (!groupId) throw new Error('groupId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewGroup(groupId, this._userId);
  }

  approvedGroup(groupId: number): Observable<any> {
    if (!groupId) throw new Error('groupId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.approvedGroup(groupId, this._userId);
  }

  unapproveDenyGroup(groupId: number, reason: any): Observable<any> {
    if (!groupId) throw new Error('groupId is required');
    if (!this._userId) throw new Error('userId is required');
    const postData = { deny_reason: reason, deny_by_id: this._userId };
    return this._acceptDenyService.unapproveGroup(groupId, postData);
  }

  viewAcceptedGroup(groupId: number): Observable<any> {
    if (!groupId) throw new Error('groupId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewAcceptedGroup(groupId, this._userId);
  }

  readPublishedGroup(groupId: number): Observable<any> {
    if (!groupId) throw new Error('groupId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.readPublishedGroup(groupId, this._userId);
  }

  deleteGroup(groupId: number): Observable<any> {
    if (!groupId) throw new Error('groupId is required');
    return this._acceptDenyService.deleteGroup(groupId);
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ NEWS ----------------------------------------
  // ----------------------------------------------------------------------------------

  acceptNews(newsId: number): Observable<any> {
    if (!newsId) throw new Error('newsId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.acceptNews(newsId, this._userId);
  }

  acceptUpdateNews(newsId: number): Observable<any> {
    if (!newsId) throw new Error('newsId is required');
    return this._acceptDenyService.acceptUpdateNews(newsId);
  }

  denyNews(newsId: number, reason: any): Observable<any> {
    if (!newsId) throw new Error('newsId is required');
    if (!this._userId) throw new Error('userId is required');
    const postData = { deny_reason: reason, deny_by_id: this._userId };
    return this._acceptDenyService.denyNews(newsId, postData);
  }

  viewApprovePublishNewsByAdmin(newsId: number): Observable<any> {
    if (!newsId) throw new Error('newsId is required');
    return this._acceptDenyService.viewApprovePublishNewsByAdmin(newsId);
  }

  viewApproveUpdateNewsByAdmin(newsId: number): Observable<any> {
    if (!newsId) throw new Error('newsId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewApproveUpdateNewsByAdmin(newsId, this._userId);
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ EVENTS --------------------------------------
  // ----------------------------------------------------------------------------------

  approvedEvents(eventId: number): Observable<any> {
    if (!eventId) throw new Error('eventId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.approvedEvents(eventId, this._userId);
  }

  approvedUpdateEvents(eventId: number): Observable<any> {
    if (!eventId) throw new Error('eventId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.approvedUpdateEvents(eventId, this._userId);
  }

  unapprovedEvent(eventId: number, reason: string): Observable<any> {
    if (!eventId) throw new Error('eventId is required');
    if (!this._userId) throw new Error('userId is required');
    const postData = { deny_reason: reason, deny_by_id: this._userId };
    return this._acceptDenyService.unapprovedEvent(eventId, postData);
  }

  denyEvent(eventId: number): Observable<any> {
    if (!eventId) throw new Error('eventId is required');
    if (!this._userId) throw new Error('userId is required');
    const postData = { deny_reason: '', deny_by_id: this._userId };
    return this._acceptDenyService.denyEvent(eventId, postData);
  }

  acceptInvitedEvent(eventId: number, selectedEventDates: any): Observable<any> {
    if (!eventId) throw new Error('eventId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.acceptInvitedEvent(eventId, this._userId, selectedEventDates);
  }

  denyInvitedEvent(eventId: number): Observable<any> {
    if (!eventId) throw new Error('eventId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.denyInvitedEvent(eventId, this._userId);
  }

  viewApprovePublishEventByAdmin(eventId: number): Observable<any> {
    if (!eventId) throw new Error('eventId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewApprovePublishEventByAdmin(eventId, this._userId);
  }

  viewApproveUpdateEventByAdmin(eventId: number): Observable<any> {
    if (!eventId) throw new Error('eventId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewApproveUpdateEventByAdmin(eventId, this._userId);
  }

  viewUpdateEvent(eventId: number): Observable<any> {
    if (!eventId) throw new Error('eventId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewUpdateEvent(eventId, this._userId);
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ TASKS ---------------------------------------
  // ----------------------------------------------------------------------------------

  adminApprovedTasks(taskId: number): Observable<any> {
    if (!taskId) throw new Error('taskId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.adminApprovedTasks(taskId, this._userId);
  }

  adminUnapprovedTasks(taskId: number, reason: any): Observable<any> {
    if (!taskId) throw new Error('taskId is required');
    if (!this._userId) throw new Error('userId is required');
    const postData = { deny_reason: reason, deny_by_id: this._userId };
    return this._acceptDenyService.adminUnapprovedTasks(taskId, postData);
  }

  adminApprovedUpdateTasks(taskId: number): Observable<any> {
    if (!taskId) throw new Error('taskId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.adminApprovedUpdateTasks(taskId, this._userId);
  }

  deleteTask(taskId: number): Observable<any> {
    if (!taskId) throw new Error('taskId is required');
    return this._acceptDenyService.deleteTask(taskId);
  }

  deleteUpdateTask(taskId: number): Observable<any> {
    if (!taskId) throw new Error('taskId is required');
    return this._acceptDenyService.deleteUpdateTask(taskId);
  }

  acceptInvitedTask(taskId: number): Observable<any> {
    if (!taskId) throw new Error('taskId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.acceptInvitedTask(taskId, this._userId);
  }

  acceptInvitedSubTask(taskId: number): Observable<any> {
    if (!taskId) throw new Error('taskId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.acceptInvitedSubTask(taskId, this._userId);
  }

  viewUpdatedTask(taskId: number): Observable<any> {
    if (!taskId) throw new Error('taskId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewUpdatedTask(taskId, this._userId);
  }

  viewCompletedTask(taskId: number): Observable<any> {
    if (!taskId) throw new Error('taskId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewCompletedTask(taskId);
  }

  viewAcceptPublishTaskByAdmin(taskId: number): Observable<any> {
    if (!taskId) throw new Error('taskId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewAcceptPublishTaskByAdmin(taskId, this._userId);
  }

  viewAcceptUpdatedTaskByAdmin(taskId: number): Observable<any> {
    if (!taskId) throw new Error('taskId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewAcceptUpdatedTaskByAdmin(taskId, this._userId);
  }

  // ----------------------------------------------------------------------------------
  // ---------------------------------- MESSAGES --------------------------------------
  // ----------------------------------------------------------------------------------

  acceptMessage(esdb_id: number): Observable<any> {
    if (!esdb_id) throw new Error('esdb_id is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.acceptMessage(esdb_id);
  }

  denyMessage(esdb_id: number): Observable<any> {
    if (!esdb_id) throw new Error('esdb_id is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.denyMessage(esdb_id);
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ ROOMS ---------------------------------------
  // ----------------------------------------------------------------------------------

  approvedRooms(roomId: number): Observable<any> {
    if (!roomId) throw new Error('roomId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.approvedRooms(roomId, this._userId);
  }

  approvedUpdateRooms(roomId: number): Observable<any> {
    if (!roomId) throw new Error('roomId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.approvedUpdateRooms(roomId, this._userId);
  }

  unapprovedRooms(roomId: number, reason: any): Observable<any> {
    if (!roomId) throw new Error('roomId is required');
    if (!this._userId) throw new Error('userId is required');
    const postData = { deny_reason: reason, deny_by_id: this._userId };
    return this._acceptDenyService.unapprovedRooms(roomId, postData);
  }

  viewApprovePublishRoomByAdmin(roomId: number): Observable<any> {
    if (!roomId) throw new Error('roomId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewApprovePublishRoomByAdmin(roomId, this._userId);
  }

  viewApproveUpdateRoomByAdmin(roomId: number): Observable<any> {
    if (!roomId) throw new Error('roomId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewApproveUpdateRoomByAdmin(roomId, this._userId);
  }

  // ----------------------------------------------------------------------------------
  // --------------------------------- INSTRUCTORS ------------------------------------
  // ----------------------------------------------------------------------------------

  approvedInstructors(instructor_id: number): Observable<any> {
    if (!instructor_id) throw new Error('instructor_id is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.approvedInstructors(instructor_id, this._userId);
  }

  approvedUpdateInstructors(instructor_id: number): Observable<any> {
    if (!instructor_id) throw new Error('instructor_id is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.approvedUpdateInstructors(instructor_id, this._userId);
  }

  unapprovedInstuctors(instructor_id: number, reason: any): Observable<any> {
    if (!instructor_id) throw new Error('instructor_id is required');
    if (!this._userId) throw new Error('userId is required');
    const postData = { deny_reason: reason, deny_by_id: this._userId };
    return this._acceptDenyService.unapprovedInstuctors(instructor_id, postData);
  }

  viewApproveUpdateInstructorByAdmin(instructorId: number, userId: number): Observable<any> {
    if (!instructorId) throw new Error('instructorId is required');
    if (!userId) throw new Error('userId is required');
    return this._acceptDenyService.viewApproveUpdateInstructorByAdmin(instructorId, userId);
  }

  viewApprovePublishInstructorByAdmin(instructorId: number, userId: number): Observable<any> {
    if (!instructorId) throw new Error('instructorId is required');
    if (!userId) throw new Error('userId is required');
    return this._acceptDenyService.viewApprovePublishInstructorByAdmin(instructorId, userId);
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------- FAQS ---------------------------------------
  // ----------------------------------------------------------------------------------

  approvedFaqs(faqId: number): Observable<any> {
    if (!faqId) throw new Error('faqId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.approvedFaqs(faqId, this._userId);
  }

  approveUpdateFaqs(faqId: number): Observable<any> {
    if (!faqId) throw new Error('faqId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.approveUpdateFaqs(faqId, this._userId);
  }

  denyFaqs(faqId: number, reason: string): Observable<any> {
    if (!faqId) throw new Error('faqId is required');
    if (!this._userId) throw new Error('userId is required');
    const postData = { deny_reason: reason, deny_by_id: this._userId };
    return this._acceptDenyService.denyFaqs(faqId, postData);
  }

  readApprovedFaqs(faqId: number): Observable<any> {
    if (!faqId) throw new Error('faqId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.readApprovedFaqs(faqId, this._userId);
  }

  readUpdatedFaqs(faqId: number): Observable<any> {
    if (!faqId) throw new Error('faqId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.readUpdatedFaqs(faqId, this._userId);
  }

  approvedFaqsCategory(faqId: number): Observable<any> {
    if (!faqId) throw new Error('faqId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.approvedFaqsCategory(faqId, this._userId);
  }

  unapprovedFaqsCategory(faqId: number): Observable<any> {
    if (!faqId) throw new Error('faqId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.unapprovedFaqsCategory(faqId);
  }

  // ----------------------------------------------------------------------------------
  // ----------------------------------- SURVEYS --------------------------------------
  // ----------------------------------------------------------------------------------

  approvedSurvey(surveyId: number): Observable<any> {
    if (!surveyId) throw new Error('surveyId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.approvedSurvey(surveyId, this._userId);
  }

  unapprovedSurvey(surveyId: number, reason: any): Observable<any> {
    if (!surveyId) throw new Error('surveyId is required');
    if (!this._userId) throw new Error('userId is required');
    const postData = { deny_reason: reason, deny_by_id: this._userId };
    return this._acceptDenyService.unapprovedSurvey(surveyId, postData);
  }

  approvedUpdatedSurvey(surveyId: number): Observable<any> {
    if (!surveyId) throw new Error('surveyId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.approvedUpdatedSurvey(surveyId, this._userId);
  }

  acceptInvitedSurvey(surveyId: number): Observable<any> {
    if (!surveyId) throw new Error('surveyId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.acceptInvitedSurvey(surveyId, this._userId);
  }

  viewAcceptPublishSurveyByAdmin(surveyId: number): Observable<any> {
    if (!surveyId) throw new Error('surveyId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewAcceptPublishSurveyByAdmin(surveyId, this._userId);
  }

  viewPublishUpdateSurveyByAdmin(surveyId: number): Observable<any> {
    if (!surveyId) throw new Error('surveyId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewPublishUpdateSurveyByAdmin(surveyId, this._userId);
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ COURSES -------------------------------------
  // ----------------------------------------------------------------------------------

  acceptInvitedCourse(courseId: number): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.acceptInvitedCourse(courseId, this._userId);
  }

  denyInvitedCourse(courseId: number): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.denyInvitedCourse(courseId, this._userId);
  }

  acceptJoinUser(courseId: number, userId: number): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.acceptJoinUser(courseId, userId || this._userId);
  }

  denyJoinUser(courseId: number): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.denyJoinUser(courseId, this._userId);
  }

  approvedCourses(courseId: number): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.approvedCourses(courseId, this._userId);
  }

  approvedUpdateCourses(courseId: number): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.approvedUpdateCourses(courseId, this._userId);
  }

  unapprovedCourse(courseId: number, reason: string): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    if (!this._userId) throw new Error('userId is required');
    const postData = { deny_reason: reason, deny_by_id: this._userId };
    return this._acceptDenyService.unapprovedCourse(courseId, postData);
  }

  deleteUpdateCourse(courseId: number): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    return this._acceptDenyService.deleteUpdateCourse(courseId);
  }

  deleteCourse(courseId: number): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    return this._acceptDenyService.deleteCourse(courseId);
  }

  viewJoinedCourse(courseId: number): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewJoinedCourse(courseId, this._userId);
  }

  viewUpdateCourse(courseId: number): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewUpdateCourse(courseId, this._userId);
  }

  viewInternalCourse(courseId: number): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewInternalCourse(courseId, this._userId);
  }

  viewInternalUpdateCourse(courseId: number): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewInternalUpdateCourse(courseId, this._userId);
  }

  viewApprovePublishCourseByAdmin(courseId: number): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewApprovePublishCourseByAdmin(courseId, this._userId);
  }

  viewApproveUpdateCourseByAdmin(courseId: number): Observable<any> {
    if (!courseId) throw new Error('courseId is required');
    if (!this._userId) throw new Error('userId is required');
    return this._acceptDenyService.viewApproveUpdateCourseByAdmin(courseId, this._userId);
  }
}
